'use client';

import type { Voice } from 'ml-text-service-client';
import { JSX, useEffect, useState } from 'react';

import { Text } from '@videoblocks/design-system';

import { calculateAudioDuration } from '../../../../../utils/calculateAudioDuration';
import { ContributorVoiceList } from '../_components/ContributorVoiceList';
import CreditConfirmationModal from '../_components/CreditConfirmationModal';
import GenerateButton from '../_components/GenerateButton';
import GeneratedVoicePlayer from '../_components/GeneratedVoicePlayer';
import Modal from '../_components/Modal';
import UploadInput from '../_components/UploadInput';
import VoiceList from '../_components/VoiceList';
import { useVoiceoverContext } from '../_context/VoiceoverContext';
import { TAB_TO_EVENT_TYPE, Tabs, useVoiceoverTabContext } from '../_context/VoiceoverTabContext';

export const createVoiceChangerFormData = (
    userFile: File,
    selectedVoiceId: string,
    userId: number | undefined,
    accountCode: string,
): FormData => {
    const formData = new FormData();
    formData.append('file', userFile);
    formData.append('voiceId', selectedVoiceId);
    formData.append('userId', String(userId || ''));
    formData.append('accountCode', accountCode);
    return formData;
};

const VoiceChanger = (): JSX.Element => {
    const {
        voices,
        contributorVoices,
        user,
        CreditsApi: { calculateCost },
        accountCode,
    } = useVoiceoverContext();
    const {
        updateState,
        playingVoiceId,
        setPlayingVoiceId,
        voiceChanger: {
            uploadedAudioUrl,
            uploadedAudioHasPlayed,
            generatedAudioUrl,
            generatedAudioHasPlayed,
            generatedAudioIsComplete,
            generatedAudioIsFetching,
            selectedVoice,
            userFile,
            aiContentId,
            lastAiContentId,
        },
    } = useVoiceoverTabContext();

    const [isDeleteModalOpen, setDeleteModalIsOpen] = useState(false);
    const [calculatedCost, setCalculatedCost] = useState(0);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);

    // Find selected voice in either AI voices or contributor voices
    const selectedAiVoice = voices.find((voice) => voice.voice_id === selectedVoice);
    const selectedContributorVoice = contributorVoices.find((voice) => voice.voice_id === selectedVoice);
    const selectedVoiceData = selectedAiVoice ?? selectedContributorVoice;
    const setSelectedVoice = (voiceId: Voice['voice_id']) => updateState({ selectedVoice: voiceId });

    const openDeleteModal = () => setDeleteModalIsOpen(true);
    const closeDeleteModal = () => setDeleteModalIsOpen(false);

    const handleDeleteAudio = () => {
        updateState({ uploadedAudioUrl: null, userFile: null });
        closeDeleteModal();
    };

    useEffect(() => {
        const getCalculatedCost = async () => {
            if (!userFile) return 0;
            try {
                const duration = await calculateAudioDuration(userFile);
                const cost = await calculateCost(duration);
                return Math.abs(cost);
            } catch (err) {
                console.error('calculateCost failed', err);
            }
            return 0;
        };

        void (async () => {
            const cost = await getCalculatedCost();
            setCalculatedCost(cost);
        })();
    }, [userFile, calculateCost]);

    return (
        <>
            {selectedVoiceData && (
                <CreditConfirmationModal
                    isOpen={confirmModalOpen}
                    setIsOpen={setConfirmModalOpen}
                    creditCost={calculatedCost}
                    buttonText="Generate Voice"
                    generateRequestBody={() =>
                        createVoiceChangerFormData(userFile!, selectedVoiceData.voice_id, user?.id, accountCode || '')
                    }
                    onSuccess={() => updateState({ uploadedAudioUrl: null, userFile: null })}
                />
            )}
            <div className="text-center">
                <div className="flex flex-col items-end">
                    {uploadedAudioUrl && (
                        <GeneratedVoicePlayer
                            header="Preview your uploaded audio, then select a voice style below to transform it."
                            audioUrl={uploadedAudioUrl}
                            hasPlayed={uploadedAudioHasPlayed}
                            setHasPlayed={() => updateState({ uploadedAudioHasPlayed: true })}
                            title="Uploaded Audio"
                            onDelete={openDeleteModal}
                        />
                    )}
                    {generatedAudioUrl && (
                        <div className="w-full mb-5">
                            <GeneratedVoicePlayer
                                audioUrl={generatedAudioUrl}
                                hasPlayed={generatedAudioHasPlayed}
                                setHasPlayed={() => updateState({ generatedAudioHasPlayed: true })}
                                setHasDownloaded={() => updateState({ generatedAudioHasDownloaded: true })}
                                isComplete={generatedAudioIsComplete}
                                isFetching={generatedAudioIsFetching}
                                title="Generated Audio"
                                aiContentId={aiContentId}
                                voiceId={selectedVoice}
                                aiContentVoiceoverType={TAB_TO_EVENT_TYPE[Tabs.VoiceChanger]}
                                lastAiContentId={lastAiContentId}
                            />
                        </div>
                    )}
                    {uploadedAudioUrl && (
                        <>
                            <GenerateButton
                                className="mt-4"
                                onSubmit={() => setConfirmModalOpen(true)}
                                calculatedCost={calculatedCost}
                            >
                                Generate Voice
                            </GenerateButton>
                        </>
                    )}
                </div>

                {!uploadedAudioUrl && (
                    <UploadInput
                        title="Upload a recording of your voice to transform it to a different voice style."
                        handleFileUpload={(file: File) => {
                            const uploadedUrl = URL.createObjectURL(file);
                            updateState({
                                uploadedAudioUrl: uploadedUrl,
                                generatedAudioUrl: null,
                                userFile: file,
                            });
                        }}
                    />
                )}
            </div>
            <ContributorVoiceList
                selectedVoice={selectedVoice}
                setSelectedVoice={setSelectedVoice}
                playingVoiceId={playingVoiceId}
                setPlayingVoiceId={setPlayingVoiceId}
            />
            <div className="mt-20">
                <VoiceList
                    selectedVoice={selectedVoice}
                    setSelectedVoice={setSelectedVoice}
                    playingVoiceId={playingVoiceId}
                    setPlayingVoiceId={setPlayingVoiceId}
                />
            </div>
            <Modal
                title="Delete audio and voiceover?"
                body={
                    <>
                        <Text fontWeight="regular">
                            Removing your uploaded audio will also delete your AI-generated voiceover.
                        </Text>
                        <Text fontWeight="regular">
                            {`If you continue, you'll need to start fresh from the beginning.`}
                        </Text>
                        <Text fontWeight="regular">{`This action is permanent and can't be undone.`}</Text>
                    </>
                }
                primaryActionText="Delete audio & voiceover"
                isOpen={isDeleteModalOpen}
                onClose={closeDeleteModal}
                onConfirm={handleDeleteAudio}
            />
        </>
    );
};

export default VoiceChanger;
